import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KeyRound, MapPin, RotateCw, ShieldCheck, UserX } from 'lucide-react';
import type { PermissionKey } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { permissionLabel } from '@/lib/access/labels';
import { useAccess } from '@/lib/access/use-access';
import { listRoles, ROLES_KEY } from '@/lib/team/api';
import {
  byKey,
  grantsOf,
  SYSTEM_ROLE_DEFINITIONS,
  type IGrantingRole,
  type IRoleDefinition,
} from '@/lib/team/grants';
import { roleDescription } from '@/lib/team/labels';
import { RoleChips, ScopeChips } from '@/routes/users/access-chips';
import { EffectivePermissions } from '@/routes/users/effective-permissions';

// P7 `/profile` → "Mi acceso" (users-roles-permissions §5; any signed-in member): my roles,
// my work scope, my effective permissions grouped by §4 group, and who to ask for a change.
// Read from `useAccess()` (the console's own `GET /v1/me`), so it is live with
// `access.changed` like everything else. "Otorgado por" needs the role definitions: the
// system roles are known to every console; custom ones only when the member may read the
// roles (`roles.view` / `users.view`) — otherwise a permission no system role explains is
// attributed to the member's only custom role, or left unattributed.

export function MyAccess() {
  const { t } = useTranslation('admin');
  const access = useAccess();
  const canReadRoles = access.canAny('roles.view', 'users.view');
  const roles = useQuery({
    queryKey: ROLES_KEY,
    queryFn: listRoles,
    staleTime: 60_000,
    enabled: canReadRoles && access.ready,
  });

  const grants = useMemo(() => {
    const defs: Map<string, IRoleDefinition> = new Map(SYSTEM_ROLE_DEFINITIONS);
    if (roles.data) for (const [k, r] of byKey(roles.data)) defs.set(k, r);
    const out = grantsOf(access.roleKeys, defs);
    const unknown = access.roles.filter((r) => !defs.has(r.key));
    if (unknown.length === 1 && unknown[0]) {
      const only: IGrantingRole = unknown[0];
      for (const p of access.permissions) if (!out.has(p)) out.set(p, [only]);
    }
    return out as ReadonlyMap<PermissionKey, readonly IGrantingRole[]>;
  }, [roles.data, access.roleKeys, access.roles, access.permissions]);

  if (access.loading) {
    return (
      <div className="space-y-3" data-testid="my-access-loading" aria-busy="true">
        <span className="sr-only">{t('access.loading')}</span>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!access.ready) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm" data-testid="my-access-error">
        <p className="font-medium text-destructive">{t('access.error.title')}</p>
        <p className="text-muted-foreground">{t('access.error.description')}</p>
        <Button variant="outline" size="sm" onClick={access.refresh} disabled={access.refreshing}>
          <RotateCw className="h-4 w-4" aria-hidden />
          {t('access.error.retry')}
        </Button>
      </div>
    );
  }

  const contact = (
    <p className="text-sm text-muted-foreground" data-testid="my-access-contact">
      {t('profile.myAccess.contact', { permission: permissionLabel(t, 'users.roles.assign') })}
    </p>
  );

  if (access.noAccess) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-dashed p-4" data-testid="my-access-none">
        <p className="flex items-center gap-2 font-medium">
          <UserX className="h-4 w-4" aria-hidden />
          {t('access.noAccess.title')}
        </p>
        {contact}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="my-access">
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('profile.myAccess.roles')}
        </h2>
        <RoleChips roles={access.roles} />
        <ul className="space-y-1">
          {access.roles.map((r) => {
            const known = roles.data?.find((x) => x.key === r.key)?.description;
            const description = roleDescription(t, {
              key: r.key,
              system: r.system,
              ...(known ? { description: known } : {}),
            });
            return description ? (
              <li key={r.key} className="text-xs text-muted-foreground">
                {description}
              </li>
            ) : null;
          })}
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('profile.myAccess.scope')}
        </h2>
        <ScopeChips scopes={access.scope} />
        <p className="text-xs text-muted-foreground" data-testid="my-access-airports">
          {access.airports === '*'
            ? t('profile.myAccess.airportsAll')
            : t('profile.myAccess.airports', { list: access.airports.join(' · ') || '—' })}
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('profile.myAccess.permissions')}
        </h2>
        <EffectivePermissions permissions={access.permissions} grants={grants} testId="my-permissions" />
      </section>

      {contact}
    </div>
  );
}
